<template>
    <Modal modal-name="watchdog-product-modal" modal-class="watchdog-product-modal" size="md" :title="$t('watchdog.title')">
        <UiLoader v-if="loading" />
        <div class="watchdog__body">
            <div class="watchdog__main rounded">
                <div class="watchdog__main_description">
                    {{ $t('watchdog.description') }}
                </div>
                <form class="watchdog__form" @submit.prevent="sentForm">
                    <div class="form-group">
                        <input
                            v-model="email"
                            class="form-control form-control--silver"
                            :class="{ 'has-error': emailError, 'no-error': !emailError && email.length > 1 }"
                            type="email"
                            :placeholder="$t('watchdog.yourEmail')"
                            autofocus
                            required
                            :disabled="userIsLoggedIn"
                        />
                        <UiAlert
                            v-if="emailError && emailMessage.length > 0"
                            class="mt-2 d-inline-block"
                            style="background-color: #fff4f2"
                            color="red"
                            borderless
                            text-colored
                        >
                            {{ emailMessage }}
                        </UiAlert>
                    </div>

                    <div v-if="!userIsLoggedIn" class="form-group">
                        <label for="registration-form-agree" class="mt-1 checkbox" :class="{ 'is-active': agreement }">
                            <input
                                id="registration-form-agree"
                                v-model="agreement"
                                class="form-check-input d-none"
                                type="checkbox"
                                name="agreement"
                            />
                            <div>
                                {{ $t('watchdog.iAgree') }}
                                <a
                                    :href="$t('watchdog.termsAndConditionUrl')"
                                    class="text-primary text-decoration-underline"
                                    target="_blank"
                                >
                                    {{ $t('watchdog.termsAndCondition') }}
                                </a>
                                {{ $t('watchdog.andWith') }}
                                <a :href="$t('watchdog.processingDataUrl')" class="text-primary text-decoration-underline" target="_blank">
                                    {{ $t('watchdog.processingData') }}
                                </a>
                            </div>
                        </label>
                        <span v-if="agreementError" class="has-error">{{ $t('watchdog.registrationAgreement') }}</span>
                    </div>

                    <div v-if="!userIsLoggedIn" class="form-group d-flex">
                        <label
                            for="registration-form-newsletter-disagree"
                            class="mt-1 checkbox"
                            :class="{ 'is-active': newsletterDisagree }"
                        >
                            <input
                                id="registration-form-newsletter-disagree"
                                v-model="newsletterDisagree"
                                class="form-check-input d-none"
                                type="checkbox"
                                name="newsletterDisagree"
                            />
                            <div v-html="$t('watchdog.nwlDisagree')"></div>
                        </label>
                    </div>

                    <button type="submit" class="watchdog__primary-button" v-html="$t('watchdog.ctaModal')"></button>

                    <UiAlert v-if="result && !result.upserted" style="background-color: #fff4f2" color="red" borderless text-colored>
                        {{ result.errors.email }}
                    </UiAlert>
                    <UiAlert v-else-if="result && result.upserted" color="green" text-colored>
                        <img src="/nuxt-assets/images/icons/check-green.svg" class="check" width="16" height="16" />
                        <div>{{ $t('watchdog.watchdogProductUpserted') }}</div>
                    </UiAlert>
                </form>
            </div>
        </div>
    </Modal>
</template>

<script lang="ts" setup>
import Modal from '~/components/Modal.vue'
import FullProduct from '~/models/Product/FullProduct'
import WatchdogProductResponse from '~/models/Api/WatchdogProductResponse'
import { useCookie } from '#imports'
import { useBasketStore } from '~/stores/basket'

const basketStore = useBasketStore()

const props = defineProps({
    product: {
        type: Object as PropType<FullProduct>,
        required: true,
    },
})
const userIsLoggedIn = ref(basketStore.authUser !== null)
const loading = ref(false)
const agreement = ref(false)
const agreementError = ref(false)
const newsletterDisagree = ref(false)
const email = ref(basketStore.authUser?.email || '')
const emailError = ref(false)
const emailMessage = ref('')
const result = ref<WatchdogProductResponse | null>(null)

const sentForm = async () => {
    loading.value = true
    try {
        if (!isFormValid()) {
            loading.value = false

            return
        }
        result.value = null
        result.value = await useNuxtApp().$repository.upsertWatchdogProduct(
            props.product.id,
            email.value,
            agreement.value,
            newsletterDisagree.value
        )

        if (result.value.upserted) {
            const cookieName = 'watchdog'
            const cookieData = useCookie<{ productIds: number[] } | undefined>(cookieName).value
            const productIds = cookieData?.productIds || []
            productIds.push(props.product.id)
            useCookie(cookieName).value = JSON.stringify({
                productIds,
            })
        }
    } catch (e) {
        console.error(e)
    }
    loading.value = false
}

let timeoutId: NodeJS.Timeout
watch(email, (value) => {
    loading.value = true
    try {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(async () => {
            const response = await useNuxtApp().$repository.validateEmail(value)

            emailError.value = !response.exists
            emailMessage.value = response.message
        }, 200)
    } catch (e) {
        console.error(e)
    }
    loading.value = false
})

const isFormValid = (): boolean => {
    agreementError.value = false
    if (userIsLoggedIn.value) {
        return true
    }

    if (agreement.value === false) {
        agreementError.value = true

        return false
    }

    return !emailError.value
}
</script>

<style lang="scss">
.watchdog {
    &__body {
        input {
            &.has-error {
                border-color: $color-red-alert;
                background: $color-red-alert-light url('/nuxt-assets/icons/error-red.svg') no-repeat right 17px center;
                background-size: 17px;
            }

            &.no-error {
                border-color: $color-green-dark;
                background: $color-green-light url('/nuxt-assets/icons/check-green.svg') no-repeat right 17px center;
                background-size: 17px;
            }
        }

        .form-group {
            .has-error {
                color: $color-alert;
            }
        }
    }

    &__main_description {
        font-size: 14px;
        color: $color-dark;
        margin-bottom: 1rem;
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    &__primary-button {
        align-self: center;
        background: $color-cta;
        border: 1px solid $color-cta;
        border-radius: 100px;
        color: $color-white;
        display: inline-block;
        font-size: 14px;
        font-weight: 500;
        padding: 8px 20px;
        text-align: center;

        &:hover {
            background: $color-cta-dark;
            color: $color-white;
            cursor: pointer;
        }
    }
}
</style>
